<template>
  <LoadingForms v-if="loading" />
  <div v-else class="bg-gray-50 p-6 -m-6 min-h-screen">
    <form @submit.prevent enctype="multipart/form-data" class="space-y-6">

      <!-- Sección: Información General y Logo -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h3 class="text-2xl font-bold text-gray-900 mb-8 flex items-center border-b pb-4">
          <svg class="w-6 h-6 mr-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
          </svg>
          Información General de la Empresa
        </h3>

        <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <!-- Logo Section -->
          <div class="lg:col-span-1 space-y-4">
            <div class="text-center">
              <div
                class="w-40 h-40 mx-auto mb-4 overflow-hidden rounded-xl border-2 border-gray-300 bg-gray-50 flex items-center justify-center shadow-sm">
                <img :src="getLogoUrl(logoCurrent)" alt="Logo" class="w-full h-full object-contain"
                  v-if="logoCurrent" />
                <svg v-else class="w-20 h-20 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd"
                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                    clip-rule="evenodd" />
                </svg>
              </div>
              <p class="text-sm font-semibold text-gray-600 mb-4">Logo de la Empresa</p>
              <label for="dropzone-file"
                class="flex items-center justify-center gap-2 w-full py-3 px-4 border-2 border-blue-500 rounded-lg cursor-pointer bg-blue-500 hover:bg-blue-600 transition-all shadow-md hover:shadow-lg">
                <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
                </svg>
                <span class="text-sm font-bold text-white">Subir Logo</span>
                <input id="dropzone-file" type="file" class="hidden" accept=".png,.jpg" ref="logo"
                  @change="selectFile" />
              </label>
              <p class="text-xs text-gray-500 mt-2">PNG o JPG - Máx 2MB</p>
            </div>
          </div>

          <!-- Campos de información -->
          <div class="lg:col-span-2 grid gap-5 md:grid-cols-2">
            <label class="block">
              <span class="text-base font-semibold text-gray-800 mb-2 block">Razón Social</span>
              <input type="text"
                class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 w-full transition-all shadow-sm"
                placeholder="Razón social de la empresa" v-model="nameCompany" />
            </label>

            <label class="block">
              <span class="text-base font-semibold text-gray-800 mb-2 block">Nombre Comercial</span>
              <input type="text"
                class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 w-full transition-all shadow-sm"
                placeholder="Nombre comercial" v-model="nameComercial" />
            </label>

            <label class="block">
              <span class="text-base font-semibold text-gray-800 mb-2 block">{{ titlesByCountry.titleTypeDocument
              }}</span>
              <input type="text"
                class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 w-full transition-all shadow-sm"
                :placeholder="titlesByCountry.titleTypeDocument" v-model="ruc" />
            </label>

            <label class="block">
              <span class="text-base font-semibold text-gray-800 mb-2 block">Tipo de Empresa</span>
              <input type="text"
                class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 w-full transition-all shadow-sm"
                placeholder="Tipo de empresa" v-model="typeCompany" />
            </label>

            <label class="block md:col-span-2">
              <span class="text-base font-semibold text-gray-800 mb-2 block">Slogan</span>
              <textarea cols="30" rows="2"
                class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 w-full resize-none transition-all shadow-sm"
                placeholder="Slogan o frase representativa de la empresa" v-model="slogan"></textarea>
            </label>
          </div>
        </div>
      </div>

      <!-- Sección: Datos de Contacto y Ubicación -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h3 class="text-2xl font-bold text-gray-900 mb-8 flex items-center border-b pb-4">
          <svg class="w-6 h-6 mr-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
              clip-rule="evenodd" />
          </svg>
          Datos de Contacto y Ubicación
        </h3>

        <div class="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          <label class="block">
            <span class="text-base font-semibold text-gray-800 mb-2 block">Dirección</span>
            <input type="text"
              class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-800 w-full transition-all shadow-sm"
              placeholder="Dirección completa" v-model="address" />
          </label>

          <label class="block">
            <span class="text-base font-semibold text-gray-800 mb-2 block">Provincia</span>
            <input type="text"
              class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-800 w-full transition-all shadow-sm"
              placeholder="Provincia" v-model="province" />
          </label>

          <label class="block">
            <span class="text-base font-semibold text-gray-800 mb-2 block">Municipalidad</span>
            <input type="text"
              class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-800 w-full transition-all shadow-sm"
              placeholder="Municipalidad" v-model="municipality" />
          </label>

          <label class="block">
            <span class="text-base font-semibold text-gray-800 mb-2 block">Representante Legal</span>
            <input type="text"
              class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-800 w-full transition-all shadow-sm"
              placeholder="Nombre del representante legal" v-model="ceo" />
          </label>

          <label class="block">
            <span class="text-base font-semibold text-gray-800 mb-2 block">Teléfonos</span>
            <input type="text"
              class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-800 w-full transition-all shadow-sm"
              placeholder="Teléfonos" v-model="cellPhone" />
          </label>

          <label class="block">
            <span class="text-base font-semibold text-gray-800 mb-2 block">Correo Electrónico</span>
            <input type="email"
              class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-800 w-full transition-all shadow-sm"
              placeholder="correo@empresa.com" v-model="email" />
          </label>
        </div>
      </div>

      <!-- Sección: Configuración del Sistema -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h3 class="text-2xl font-bold text-gray-900 mb-8 flex items-center border-b pb-4">
          <svg class="w-6 h-6 mr-3 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
              clip-rule="evenodd" />
          </svg>
          Configuración del Sistema
        </h3>

        <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <label class="block" v-show="stateShowInput">
            <span class="text-base font-semibold text-gray-800 mb-2 block">N° Sucursales Permitidos</span>
            <input type="number"
              class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-800 w-full transition-all shadow-sm"
              placeholder="Número de sucursales" v-model="numberStores" />
          </label>

          <label class="block">
            <span class="text-base font-semibold text-gray-800 mb-2 block">Token del Sistema</span>
            <input type="text"
              class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-gray-800 w-full transition-all shadow-sm"
              placeholder="Token de integración" v-model="token" />
          </label>

          <div class="flex items-end space-x-6 pb-2">
            <label class="flex flex-col cursor-pointer">
              <span class="text-base font-semibold text-gray-800 mb-3 block">Estado</span>
              <div class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" class="sr-only peer" v-model="stateCompany" />
                <div
                  class="w-14 h-7 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-transparent after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500 shadow-inner">
                </div>
                <span class="ml-3 text-base font-semibold text-gray-700">{{ stateCompany ? "Activo" : "Inactivo"
                }}</span>
              </div>
            </label>

            <label class="flex flex-col cursor-pointer" v-show="stateShowInput">
              <span class="text-base font-semibold text-gray-800 mb-3 block">¿Envía Correos?</span>
              <div class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" class="sr-only peer" v-model="senEmail" />
                <div
                  class="w-14 h-7 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-transparent after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500 shadow-inner">
                </div>
                <span class="ml-3 text-base font-semibold text-gray-700">{{ senEmail ? "Sí" : "No" }}</span>
              </div>
            </label>
          </div>
        </div>
      </div>

      <!-- Sección: Certificado Digital -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <h3 class="text-2xl font-bold text-gray-900 mb-8 flex items-center border-b pb-4">
          <svg class="w-6 h-6 mr-3 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z"
              clip-rule="evenodd" />
          </svg>
          Certificado Digital
        </h3>

        <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <!-- Certificate Preview/Upload -->
          <div class="space-y-4">
            <div class="text-center">
              <div
                class="w-40 h-40 mx-auto mb-4 overflow-hidden rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 flex items-center justify-center shadow-sm">
                <div v-if="certificate.file" class="text-center p-3">
                  <svg class="w-12 h-12 text-amber-500 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                      clip-rule="evenodd" />
                  </svg>
                  <p class="text-xs font-semibold text-amber-600 truncate w-32">{{ certificate.name }}</p>
                  <p class="text-xs text-gray-500">.{{ certificate.extension }}</p>
                </div>
                <div v-else class="text-center p-3">
                  <svg class="w-16 h-16 text-gray-300 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                      clip-rule="evenodd" />
                  </svg>
                  <p class="text-sm text-gray-400">Sin certificado</p>
                </div>
              </div>
              <label for="dropzone-file-cert"
                class="flex flex-col items-center justify-center w-full h-12 border-2 border-amber-400 border-dashed rounded-lg cursor-pointer bg-amber-50 hover:bg-amber-100 transition-all shadow-sm hover:shadow">
                <div class="flex items-center justify-center w-full px-4">
                  <svg class="w-5 h-5 text-amber-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
                  </svg>
                  <span class="text-sm font-bold text-amber-700">Subir Certificado</span>
                </div>
                <input id="dropzone-file-cert" type="file" class="hidden" ref="certificateFile"
                  @change="selectFileCertificate" />
              </label>
              <p class="text-xs text-gray-500 mt-2">Archivo de certificado digital</p>
            </div>
          </div>

          <!-- Certificate Fields -->
          <div class="lg:col-span-2 grid gap-5 md:grid-cols-2">
            <label class="block">
              <span class="text-base font-semibold text-gray-800 mb-2 block">Nombre del Certificado</span>
              <input type="text"
                class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 w-full transition-all shadow-sm"
                placeholder="Nombre del certificado" v-model="certificate.name" />
            </label>

            <label class="block">
              <span class="text-base font-semibold text-gray-800 mb-2 block">Contraseña</span>
              <input type="password"
                class="py-3 px-4 text-base bg-white border-2 border-gray-300 outline-none rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-gray-800 w-full transition-all shadow-sm"
                placeholder="Contraseña del certificado" v-model="certificate.password" />
            </label>
          </div>
        </div>
      </div>

      <!-- Botones de Acción -->
      <div class="flex justify-start gap-4 pt-2">
        <button
          class="bg-red-600 hover:bg-red-700 text-white px-8 py-3.5 font-bold text-base rounded-lg shadow-lg hover:shadow-xl active:scale-95 transition-all flex items-center whitespace-nowrap"
          type="button">
          <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z"
              clip-rule="evenodd" />
          </svg>
          Cancelar
        </button>

        <button
          class="bg-blue-600 hover:bg-blue-700 text-white px-10 py-3.5 font-bold text-base rounded-lg shadow-lg hover:shadow-xl active:scale-95 transition-all flex items-center whitespace-nowrap"
          @click="insert" v-if="id === 0">
          <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd" />
          </svg>
          Registrar Empresa
        </button>

        <button
          class="bg-blue-600 hover:bg-blue-700 text-white px-10 py-3.5 font-bold text-base rounded-lg shadow-lg hover:shadow-xl active:scale-95 transition-all flex items-center whitespace-nowrap"
          @click="update" v-else>
          <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd" />
          </svg>
          Guardar Cambios
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";
import titlesByCountry from "/public/apis/titles-by-country.json";
import {
  confirmationOfRecord,
  confirmationOfUpdate,
  errorActions,
} from "../../../alerts.js";
import LoadingForms from "@/components/Loadings/LoadingForms.vue";
import axios from "../../../api";
import { localTypeUser } from "@/publicjs/localStorage.js";
export default {
  name: "DataCompany",
  components: {
    LoadingForms,
  },
  setup() {
    const APIURL = process.env.VUE_APP_API_URL_RESOURCE;
    const nameCompany = ref("");
    const nameComercial = ref("");
    const slogan = ref("");
    const ruc = ref("");
    const address = ref("");
    const ceo = ref("");
    const cellPhone = ref("");
    const typeCompany = ref("");
    const senEmail = ref(false);
    const numberStores = ref(0);
    const logo = ref("");
    const logoCurrent = ref("");
    const stateCompany = ref(false);
    const id = ref(0);
    const stateShowInput = ref(false);

    const email = ref("");
    const province = ref("");
    const municipality = ref("");
    const token = ref("");
    const certificate = ref({
      name: "",
      extension: "",
      file: "",
      password: "",
    });
    const certificateFile = ref("");

    const getLogoUrl = (logoPath) => {
      if (!logoPath) return "";
      let cleanPath = decodeURIComponent(String(logoPath).trim());
      if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
        return cleanPath;
      }
      cleanPath = cleanPath.replace(/^\/+/u, "");
      if (!cleanPath.startsWith("storage/")) {
        cleanPath = `storage/${cleanPath}`;
      }
      const base = APIURL.endsWith("/") ? APIURL : `${APIURL}/`;
      return `${base}${cleanPath}`;
    };

    const loading = ref(true);
    const getData = async () => {
      loading.value = true;
      try {
        const response = await axios
          .get("empresa")
          .catch((error) => errorActions(error));
        if (response.data.length !== 0) {
          const data = await response.data[0];
          nameCompany.value = data.razon_social;
          nameComercial.value = data.nombre_comercial;
          slogan.value = data.slogan;
          ruc.value = data.ruc;
          address.value = data.direccion;
          ceo.value = data.representante;
          cellPhone.value = data.telefonos;
          typeCompany.value = data.tipo_empresa;
          senEmail.value = data.envia_correos === 1 ? true : false;
          numberStores.value = data.numero_de_sucursales;
          logoCurrent.value = data.logo;
          stateCompany.value = data.estado === 1 ? true : false;

          email.value = data.email;
          province.value = data.province;
          municipality.value = data.municipality;
          token.value = data.token;

          certificate.value.name = data.certificate_name
          certificate.value.extension = data.certificate_extension
          certificate.value.file = data.certificate_file
          certificate.value.password = data.certificate_password

          id.value = data.id;
        }
        loading.value = false;
      } catch (error) {
        errorActions(error);
      }
    };

    // Selecciona el archivo
    const selectFile = async () => {
      await logo.value.files;
      if (id.value > 0) {
        await updateLogo();
      }
    };
    const selectFileCertificate = async () => {
      await certificateFile.value.files;
      if (id.value > 0) {
        await uploadCertificate();
      }
    };

    const insert = async () => {
      if (nameCompany.value.trim().length < 4) {
        errorActions(
          "Lo sentimos, no podemos registrar la empresa, escribe una longitud de caracteres mayor a 3"
        );
        return;
      }

      //recuperando la imagen
      const formData = new FormData();
      formData.append("razon_social", nameCompany.value);
      formData.append("nombre_comercial", nameComercial.value);
      formData.append("slogan", slogan.value);
      formData.append("ruc", ruc.value);
      formData.append("direccion", address.value);
      formData.append("representante", ceo.value);
      formData.append("telefonos", cellPhone.value);
      formData.append("tipo_empresa", typeCompany.value);
      formData.append("envia_correos", senEmail.value === true ? 1 : 0);
      formData.append("numero_de_sucursales", numberStores.value);
      formData.append("logo", logo.value.files[0]);
      formData.append("estado", stateCompany.value === true ? 1 : 0);

      formData.append("email", email.value);
      formData.append("province", province.value);
      formData.append("municipality", municipality.value);
      formData.append("token", token.value);

      formData.append("certificate_name", certificate.value.name);
      formData.append("certificate_extension", certificate.value.extension);
      formData.append("certificate_file", certificate.value.file);
      formData.append("certificate_password", certificate.value.password);


      const response = await axios
        .post("empresa", formData, {
          headers: {
            // "Content-Type": "application/json",
            "Content-Type": "multipart/form-data",
          },
        })
        .catch(function (error) {
          errorActions(error);
        });
      const data = await response.data;
      if (data.msg === true) {
        confirmationOfRecord(nameCompany.value);
        await getData();
      } else if (data.msg === "Request failed with status code 500") {
        errorActions("Request failed with status code 500");
      } else {
        errorActions(
          "Lo sentimos no pudimos realizar el registro de la empresa"
        );
      }
    };

    const update = async () => {
      if (nameCompany.value.trim().length < 4) {
        errorActions(
          "Lo sentimos, no podemos registrar la empresa, escribe una longitud de caracteres mayor a 3"
        );
        return;
      }

      //recuperando la imagen
      const dataForm = {
        razon_social: nameCompany.value,
        nombre_comercial: nameComercial.value,
        slogan: slogan.value,
        ruc: ruc.value,
        direccion: address.value,
        representante: ceo.value,
        telefonos: cellPhone.value,
        tipo_empresa: typeCompany.value,
        envia_correos: senEmail.value === true ? 1 : 0,
        numero_de_sucursales: numberStores.value,
        estado: stateCompany.value === true ? 1 : 0,

        email: email.value,
        province: province.value,
        municipality: municipality.value,
        token: token.value,

        certificate_name: certificate.value.name,
        certificate_extension: certificate.value.extension,
        certificate_file: certificate.value.file,
        certificate_password: certificate.value.password,

      };

      const response = await axios
        .put(`empresa/${id.value}`, dataForm, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .catch(function (error) {
          errorActions(error);
        });
      const data = await response.data;
      if (data.msg === true) {
        confirmationOfUpdate(nameCompany.value);
        await getData();
      } else if (data.msg === "Request failed with status code 500") {
        errorActions("Request failed with status code 500");
      } else {
        errorActions(
          "Lo sentimos no pudimos realizar el registro de la empresa"
        );
      }
    };

    const updateLogo = async () => {
      const formData = new FormData();
      formData.append("logo", logo.value.files[0]);
      // formData.append("_method", "PUT");
      const response = await axios
        .post(`empresa/updatelogo/${id.value}`, formData, {
          "Content-Type": "multipart/form-data",
        })
        .catch((error) => {
          errorActions(error);
        });
      const dataresponse = await response.data;
      if (dataresponse.msg === true) {
        confirmationOfUpdate("El logo de " + nameCompany.value);
        await getData();
      } else if (dataresponse.msg === "error") {
        errorActions("Error al guardar imagen");
      }
    };

    const uploadCertificate = async () => {
      const formData = new FormData();
      formData.append("certificate", certificateFile.value.files[0]);
      // formData.append("_method", "PUT");
      const response = await axios
        .post(`empresa/upload-certificate`, formData, {
          "Content-Type": "multipart/form-data",
        })
        .catch((error) => {
          errorActions(error);
        });
      const dataresponse = await response.data;
      if (dataresponse.msg === true) {
        confirmationOfUpdate("El certificado de " + nameCompany.value);
        certificate.value.file = dataresponse.file || null
        certificate.value.extension = dataresponse.extension || null
      } else if (dataresponse.msg === "error") {
        errorActions("Error al guardar certificado");
      }
    };

    onMounted(async () => {
      const typeUser = await localTypeUser();
      stateShowInput.value = typeUser === "SUPERADMIN_ROOT" ? true : false;
      await getData();
    });

    return {
      titlesByCountry,
      nameCompany,
      nameComercial,
      slogan,
      ruc,
      address,
      ceo,
      cellPhone,
      typeCompany,
      senEmail,
      numberStores,
      logo,
      logoCurrent,
      stateCompany,

      email,
      province,
      municipality,
      token,

      certificate,
      certificateFile,

      loading,
      selectFile,
      selectFileCertificate,
      insert,
      APIURL,
      id,
      update,
      stateShowInput,
      getLogoUrl,
    };
  },
};
</script>
